'use client';
import { useState } from 'react';
import Link from 'next/link';

const pages = [
  { id: 'about', label: 'About' },
  { id: 'contact', label: 'Contact' },
  { id: 'standards', label: 'Editorial Standards' },
  { id: 'privacy', label: 'Privacy & Terms' },
  { id: 'accessibility', label: 'Accessibility' },
  { id: 'app', label: 'App Download' },
];

const leadership = [
  { name: 'Margaret Osei-Bonsu', role: 'Editor-in-Chief', initials: 'MO' },
  { name: 'Thomas Hartley', role: 'Managing Editor', initials: 'TH' },
  { name: 'Priya Nair', role: 'Digital Director', initials: 'PN' },
];

const shortcuts = [
  { key: 'Alt + H', action: 'Go to Homepage' },
  { key: 'Alt + S', action: 'Open Search' },
  { key: 'Alt + L', action: 'Go to Live Updates' },
  { key: 'Alt + N', action: 'Go to Newsletters' },
  { key: 'Tab', action: 'Navigate interactive elements' },
  { key: 'Enter / Space', action: 'Activate button or link' },
];

export default function AboutPage() {
  const [activePage, setActivePage] = useState('about');
  const [cookiePrefs, setCookiePrefs] = useState({ functional: true, analytics: false, marketing: false });

  const pageTitles: Record<string, string> = {
    about: 'About Veritas News',
    contact: 'Contact Us',
    standards: 'Editorial Standards',
    privacy: 'Privacy & Terms',
    accessibility: 'Accessibility',
    app: 'App Download',
  };

  const pageSubtitles: Record<string, string> = {
    about: 'Independent journalism trusted by millions since 2008',
    contact: 'Get in touch with our team',
    standards: 'Our commitment to accuracy, independence and accountability',
    privacy: 'How we use and protect your data',
    accessibility: 'Making our journalism available to everyone',
    app: 'Take Veritas News with you everywhere',
  };

  return (
    <div>
      {/* HERO */}
      <div className="bg-[#1A1A1A] py-12 px-5">
        <div className="max-w-[1400px] mx-auto">
          <h1 className="font-['Playfair_Display',Georgia,serif] font-bold text-[36px] text-white mb-2">
            {pageTitles[activePage]}
          </h1>
          <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] text-white/75">
            {pageSubtitles[activePage]}
          </p>
        </div>
      </div>

      {/* TAB NAV */}
      <div className="bg-white border-b border-[#D4D4D4] sticky top-[100px] z-50">
        <div className="max-w-[1400px] mx-auto px-5 flex overflow-x-auto scrollbar-none">
          {pages.map(page => (
            <button
              key={page.id}
              onClick={() => setActivePage(page.id)}
              className={`px-5 h-12 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] whitespace-nowrap border-b-2 -mb-0.5 transition-all ${
                activePage === page.id
                  ? 'border-[#0D0D0D] text-[#0D0D0D]'
                  : 'border-transparent text-[#6B6B6B] hover:text-[#2D2D2D]'
              }`}
            >
              {page.label}
            </button>
          ))}
        </div>
      </div>

      {/* CONTENT */}
      <div className="max-w-[720px] mx-auto px-5 py-12 animate-fade-in">

        {/* ABOUT */}
        {activePage === 'about' && (
          <div>
            {[
              { title: 'Our Mission', body: 'Veritas News was founded in 2008 with a simple conviction: that rigorous, independent journalism makes democracy work. We report facts, explain context, and hold the powerful to account — without fear or favour, and without the influence of advertisers or political patrons.' },
              { title: 'Who We Are', body: 'We are a team of more than 300 journalists, editors, photographers, and technologists operating from 42 bureaux on six continents. Our reporting is grounded in primary source interviews, document analysis and on-the-ground observation. We do not publish opinion without labelling it clearly as such.' },
              { title: 'Editorial Standards', body: 'Every story published by Veritas News is subject to a minimum of two editorial reviews. We require multiple independent sources for sensitive claims, we contact all parties named in critical coverage, and we publish corrections prominently when we get things wrong.' },
              { title: 'Funding & Independence', body: 'Veritas News is funded through a combination of reader subscriptions, newsletter revenue, and a journalism endowment established in 2012. We accept no government funding and no advertising from political parties. Our editorial decisions are made exclusively by editors.' },
            ].map(section => (
              <div key={section.title} className="mb-8">
                <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] pb-2 border-b border-[#D4D4D4] mb-4">{section.title}</h2>
                <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] leading-[1.7] text-[#2D2D2D]">{section.body}</p>
              </div>
            ))}
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] pb-2 border-b border-[#D4D4D4] mb-6">Leadership</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mt-8">
              {leadership.map(person => (
                <div key={person.name} className="border border-[#D4D4D4] p-5">
                  <div className="w-16 h-16 circle-avatar bg-[#E5E7EB] flex items-center justify-center mb-3">
                    <span className="font-['Playfair_Display',Georgia,serif] font-bold text-[20px] text-[#6B6B6B]">{person.initials}</span>
                  </div>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[15px]">{person.name}</p>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#6B6B6B]">{person.role}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTACT */}
        {activePage === 'contact' && (
          <div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 mb-10">
              {[
                { title: 'News Tips', email: 'tips@veritas.news', desc: 'Share confidential information securely with our reporters.' },
                { title: 'Editorial', email: 'editorial@veritas.news', desc: 'Complaints, corrections, and editorial inquiries.' },
                { title: 'Advertising', email: 'commercial@veritas.news', desc: 'Brand partnerships and commercial opportunities.' },
              ].map(c => (
                <div key={c.title} className="border border-[#D4D4D4] p-5">
                  <h3 className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[13px] uppercase tracking-[0.06em] mb-2">{c.title}</h3>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] text-[#1A4A8A] mb-2">{c.email}</p>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B] leading-[1.5]">{c.desc}</p>
                </div>
              ))}
            </div>
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] pb-2 border-b border-[#D4D4D4] mb-6">Send a message</h2>
            <div className="space-y-4 max-w-[600px]">
              {[
                { id: 'contact-name', label: 'Your name', type: 'text', ph: 'Full name' },
                { id: 'contact-email', label: 'Email address', type: 'email', ph: 'you@example.com' },
              ].map(f => (
                <div key={f.id}>
                  <label className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] block mb-1.5" htmlFor={f.id}>{f.label}</label>
                  <input id={f.id} type={f.type} placeholder={f.ph} className="border border-[#D4D4D4] px-4 py-3 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] w-full focus:border-[#0D0D0D] focus:outline-none" />
                </div>
              ))}
              <div>
                <label className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] block mb-1.5" htmlFor="contact-subject">Subject</label>
                <select id="contact-subject" className="border border-[#D4D4D4] px-4 py-3 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] w-full focus:border-[#0D0D0D] focus:outline-none bg-white">
                  <option>Select a subject</option>
                  <option>News tip</option>
                  <option>Correction request</option>
                  <option>Complaint</option>
                  <option>General inquiry</option>
                </select>
              </div>
              <div>
                <label className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] block mb-1.5" htmlFor="contact-message">Message</label>
                <textarea id="contact-message" rows={6} placeholder="Your message..." className="border border-[#D4D4D4] px-4 py-3 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] w-full focus:border-[#0D0D0D] focus:outline-none resize-none" />
              </div>
              <button className="bg-[#C8102E] text-white px-6 py-3 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[14px] hover:bg-[#9E0C23] transition-colors">
                Send Message
              </button>
            </div>
          </div>
        )}

        {/* STANDARDS */}
        {activePage === 'standards' && (
          <div>
            {[
              { title: 'Accuracy', body: 'We verify all claims before publication. Factual assertions require at least one independent source. Statistical claims are verified against primary data. We clearly distinguish between confirmed facts, official statements, and allegations.' },
              { title: 'Independence', body: 'Our editorial decisions are made exclusively by editors, free from the influence of advertisers, shareholders, or political interests. We decline any advertising or partnership that we believe could compromise our editorial integrity.' },
              { title: 'Sources & Verification', body: 'We name sources wherever possible and explain why we grant anonymity when necessary. We do not use anonymous sources for stories that could be told on the record. We verify documents before publishing them.' },
              { title: 'Corrections Policy', body: 'We correct errors promptly, prominently, and transparently. Corrections are published on the original article and noted in our corrections log. We do not silently amend articles. If a story requires significant revision, we publish an editor\'s note explaining what changed and why.' },
              { title: 'Complaints Procedure', body: 'Readers who believe we have been inaccurate, unfair or in breach of our standards should write to editorial@veritas.news. We aim to respond within five working days. If you are not satisfied with our response, you may escalate to the Independent Press Standards Organisation (IPSO).' },
            ].map(s => (
              <div key={s.title} className="mb-8">
                <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] pb-2 border-b border-[#D4D4D4] mb-4">{s.title}</h2>
                <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] leading-[1.7] text-[#2D2D2D]">{s.body}</p>
              </div>
            ))}
          </div>
        )}

        {/* PRIVACY */}
        {activePage === 'privacy' && (
          <div>
            <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] leading-[1.7] text-[#2D2D2D] mb-8">
              Veritas News is committed to protecting your personal information. We collect only the data we need, we never sell it to third parties, and we give you full control over how it is used.
            </p>
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] pb-2 border-b border-[#D4D4D4] mb-6">Cookie Preferences</h2>
            <div className="space-y-3 mb-8">
              {[
                { key: 'functional' as const, title: 'Functional', desc: 'Required for the site to work. Cannot be disabled.' },
                { key: 'analytics' as const, title: 'Analytics', desc: 'Help us understand how you use the site, so we can improve it.' },
                { key: 'marketing' as const, title: 'Marketing', desc: 'Allow us to show you relevant promotions on third-party platforms.' },
              ].map(cookie => (
                <div key={cookie.key} className="flex justify-between items-center border border-[#D4D4D4] p-4">
                  <div>
                    <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[14px]">{cookie.title}</p>
                    <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">{cookie.desc}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={cookiePrefs[cookie.key]}
                    disabled={cookie.key === 'functional'}
                    onChange={e => setCookiePrefs(prev => ({ ...prev, [cookie.key]: e.target.checked }))}
                    className="w-5 h-5 cursor-pointer"
                    aria-label={`Toggle ${cookie.title} cookies`}
                  />
                </div>
              ))}
            </div>
            <button className="bg-[#C8102E] text-white px-6 py-3 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[14px] hover:bg-[#9E0C23] transition-colors">Save Preferences</button>
          </div>
        )}

        {/* ACCESSIBILITY */}
        {activePage === 'accessibility' && (
          <div>
            <p className="font-['Source_Serif_4',Georgia,serif] text-[16px] leading-[1.7] text-[#2D2D2D] mb-8">
              Veritas News is committed to making our journalism accessible to everyone. We aim to conform to WCAG 2.1 Level AA accessibility standards across all our digital products.
            </p>
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] pb-2 border-b border-[#D4D4D4] mb-6">Keyboard Shortcuts</h2>
            <table className="w-full border-collapse font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] mt-4">
              <thead>
                <tr className="bg-[#F5F4F0]">
                  <th className="text-left p-3 font-bold text-[11px] uppercase tracking-[0.08em] border-b border-[#D4D4D4]">Shortcut</th>
                  <th className="text-left p-3 font-bold text-[11px] uppercase tracking-[0.08em] border-b border-[#D4D4D4]">Action</th>
                </tr>
              </thead>
              <tbody>
                {shortcuts.map(s => (
                  <tr key={s.key} className="border-b border-[#D4D4D4]">
                    <td className="p-3"><code className="bg-[#F5F4F0] px-2 py-0.5 font-mono text-[13px]">{s.key}</code></td>
                    <td className="p-3 text-[#2D2D2D]">{s.action}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[13px] text-[#6B6B6B] mt-6">
              To report an accessibility issue, email us at <span className="text-[#1A4A8A]">accessibility@veritas.news</span>
            </p>
          </div>
        )}

        {/* APP */}
        {activePage === 'app' && (
          <div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-10">
              <div>
                <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[28px] mb-4">Veritas on every device</h2>
                <ul className="space-y-3 mb-6">
                  {['Offline reading', 'Breaking alerts', 'Ad-lite experience', 'Personalised feed', 'Save stories'].map(f => (
                    <li key={f} className="flex items-center gap-2 font-['IBM_Plex_Sans',system-ui,sans-serif] text-[14px] text-[#2D2D2D]">
                      <span className="text-[#C8102E]">✓</span> {f}
                    </li>
                  ))}
                </ul>
                <div className="flex gap-3">
                  {['App Store', 'Google Play'].map(store => (
                    <button key={store} className="border-2 border-[#0D0D0D] px-5 py-2.5 font-['IBM_Plex_Sans',system-ui,sans-serif] font-semibold text-[13px] hover:bg-[#0D0D0D] hover:text-white transition-all">
                      {store}
                    </button>
                  ))}
                </div>
              </div>
              <div className="flex items-center justify-center bg-[#F5F4F0] p-8">
                <div className="w-32 border-2 border-[#D4D4D4] aspect-[9/19] flex items-center justify-center text-[#888] font-['IBM_Plex_Sans',system-ui,sans-serif] text-[11px]">
                  App Mockup
                </div>
              </div>
            </div>
            <h2 className="font-['Playfair_Display',Georgia,serif] font-bold text-[22px] pb-2 border-b border-[#D4D4D4] mb-6">What readers say</h2>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
              {[
                { quote: 'The best news app I have used. The offline feature alone is worth the subscription.', name: 'R. Okafor', platform: 'App Store' },
                { quote: 'Breaking alerts are genuinely useful — not clickbait. A rare thing.', name: 'Anna K.', platform: 'Google Play' },
                { quote: 'Finally an app that feels like it was designed for reading rather than engagement.', name: 'Tom H.', platform: 'App Store' },
              ].map((t, i) => (
                <div key={i} className="border border-[#D4D4D4] p-5">
                  <p className="font-['Source_Serif_4',Georgia,serif] italic text-[14px] text-[#2D2D2D] leading-[1.6] mb-3">&ldquo;{t.quote}&rdquo;</p>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] font-bold text-[13px]">{t.name}</p>
                  <p className="font-['IBM_Plex_Sans',system-ui,sans-serif] text-[12px] text-[#6B6B6B]">{t.platform}</p>
                </div>
              ))}
            </div>
          </div>
        )}

      </div>
    </div>
  );
}
